/**
 * Common Utility Methods
 *
 * Centralized utility functions used across the application.
 * Following senior developer best practices:
 * - Pure functions where possible
 * - Clear naming and JSDoc comments
 * - Single responsibility principle
 * - Error handling
 */

import React from "react";
import { notification, Tooltip } from "antd";
import { StyledTruncatedText } from "styles/global.styled";
import ContractService from "services/ContractService";
import dayjs from "dayjs";
import { UploadStatus } from "constants/AppConstant";
import { DownloadOutlined } from "@ant-design/icons";
import store from "store";
import MasterService from "services/MasterService";

// ============================================
// API & DATA FETCHING UTILITIES
// ============================================

/**
 * Fetch all pages from paginated API endpoint
 * @param {Function} apiCallback - API function to call
 * @param {string} dataKey - Key to extract data from response
 * @param {Object} payload - Additional payload for API call
 * @returns {Promise<Array>} - Combined array of all pages
 *
 * Usage:
 * const allContracts = await fetchAllPages(
 *   ContractService.getContracts,
 *   'contracts',
 *   { filter: 'active' }
 * );
 */
export const fetchAllPages = async (apiCallback, dataKey, payload = {}) => {
  let page = 1;
  let list = [];

  while (true) {
    const response = await apiCallback({
      ...payload,
      page,
    });

    const chunk = response?.data?.[dataKey] || [];
    list = [...list, ...chunk];

    if (!response?.data?.pagination?.next) break;

    page++;
  }

  return list;
};

/**
 * Transform dropdown options for use with i18n
 * @param {Array} options - Array of {value, labelKey} objects
 * @param {Function} t - i18n translation function
 * @returns {Array} - Array of {value, label} objects with translated labels
 *
 * Usage:
 * const translatedOptions = getTranslatedOptions(SCHEDULE_STATUS_OPTIONS, t);
 */
export const getTranslatedOptions = (options, t) => {
  return options.map((option) => ({
    value: option.value,
    label: t(option.labelKey) || option.value,
  }));
};

// ============================================
// STRING FORMATTING UTILITIES
// ============================================

/**
 * Convert string to title case
 * Handles underscores and hyphens, converts to spaces
 * @param {string} str - Input string
 * @returns {string} - Title cased string
 *
 * Examples:
 * "hello_world" -> "Hello World"
 * "in-progress" -> "In Progress"
 */
export const toTitleCase = (str) => {
  if (!str) return "";
  return str
    .toString()
    .trim()
    .replace(/[-_]+/g, " ")
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

/**
 * Truncate string to specified length with ellipsis
 * @param {string} str - Input string
 * @param {number} maxLength - Maximum length (default 50)
 * @returns {string} - Truncated string
 */
export const truncateString = (str, maxLength = 50) => {
  if (!str || str.length <= maxLength) return str;
  return `${str.substring(0, maxLength)}...`;
};

// ============================================
// STATUS & CLASS UTILITIES
// ============================================

/**
 * Get CSS class for status tag
 * @param {string} status - Status value
 * @returns {string} - CSS class name
 *
 * Used in: Tables, status displays
 */
export const getStatusClass = (status) => {
  const statusMap = {
    "in progress": "status-tag status-in-progress",
    draft: "status-tag status-draft",
    completed: "status-tag status-completed",
    active: "status-tag status-active",
    inactive: "status-tag status-inactive",
    pending: "status-tag status-pending",
    delayed: "status-tag status-delayed",
  };

  return statusMap[status?.toLowerCase()] || "status-tag";
};

/**
 * Get status color for progress indicators
 * @param {string} status - Status value
 * @returns {string} - Color hex code
 */
export const getStatusColor = (status) => {
  const colorMap = {
    pending: "#f29a2e",
    in_progress: "#4f88ff",
    "in progress": "#4f88ff",
    completed: "#18c47c",
    delayed: "#e53935",
    draft: "#e53935",
    active: "#18c47c",
    inactive: "#e53935",
  };

  return colorMap[status?.toLowerCase()] || "#9aa4b2";
};

export const formatDocumentDownload = (
  file_name,
  document_id,
  model_name,
  contract_id,
) => {
  if (!file_name || !document_id || !model_name || !contract_id) return "-";

  const handleDownload = (e) => {
    e.preventDefault();

    downloadDocument({
      documentId: document_id,
      modelName: model_name,
      contractId: contract_id,
    });
  };

  return (
    <a
      href="#"
      onClick={handleDownload}
      style={{
        color: "#1677ff",
        display: "flex",
        alignItems: "center",
        gap: "6px",
        cursor: "pointer",
      }}
    >
      <DownloadOutlined style={{ fontSize: "18px" }} />
      <span>{file_name}</span>
    </a>
  );
};

// ============================================
// DATE UTILITIES
// ============================================

/**
 * Format date for display in DD-MM-YYYY format
 * @param {string|Date|dayjs} date - Input date
 * @param {string} format - Date format (default: "DD-MM-YYYY")
 * @returns {string} - Formatted date string
 *
 * Examples:
 * formatDate("2024-01-15") -> "15-01-2024"
 * formatDate("2024-01-15", "DD-MM-YY") -> "15-01-24"
 */
export const formatDate = (date, format = "DD-MM-YYYY") => {
  if (!date) return "-";
  return dayjs(date).format(format);
};

/**
 * Format date for API submission (YYYY-MM-DD)
 * @param {dayjs|Date|string} date - Date object
 * @returns {string|null} - Formatted date string or null
 *
 * Example: formatDateForAPI("15-01-2024") -> "2024-01-15"
 */
export const formatDateForAPI = (date) => {
  if (!date) return null;
  return dayjs(date).format("YYYY-MM-DD");
};

/**
 * Format date for display from API response
 * Converts API date (YYYY-MM-DD) to display format (DD-MM-YYYY)
 * @param {string} apiDate - Date string from API (YYYY-MM-DD)
 * @returns {string} - Formatted date string (DD-MM-YYYY)
 *
 * Example: formatDateFromAPI("2024-01-15") -> "15-01-2024"
 */
export const formatDateFromAPI = (apiDate) => {
  if (!apiDate) return "-";
  return dayjs(apiDate).format("DD-MM-YYYY");
};

/**
 * Format date with time for display
 * @param {string|Date|dayjs} dateTime - Input date/time
 * @returns {string} - Formatted date-time string
 *
 * Example: formatDateTime("2024-01-15T14:30:00Z") -> "15-01-2024 14:30"
 */
export const formatDateTime = (dateTime) => {
  if (!dateTime) return "-";
  return dayjs(dateTime).format("DD-MM-YYYY HH:mm");
};

/**
 * Check if end date is after start date
 * @param {dayjs} startDate - Start date
 * @param {dayjs} endDate - End date
 * @returns {boolean} - True if valid
 */
export const isEndDateValid = (startDate, endDate) => {
  if (!startDate || !endDate) return true;
  return dayjs(endDate).isAfter(dayjs(startDate));
};

// ============================================
// VALIDATION UTILITIES
// ============================================

/**
 * Validate file size
 * @param {File} file - File object
 * @param {number} maxSizeMB - Maximum size in MB
 * @returns {boolean} - True if valid
 */
export const isFileSizeValid = (file, maxSizeMB = 50) => {
  if (!file) return false;
  const maxBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxBytes;
};

/**
 * Validate file extension
 * @param {File} file - File object
 * @param {Array<string>} allowedTypes - Array of allowed extensions
 * @returns {boolean} - True if valid
 */
export const isFileTypeValid = (file, allowedTypes) => {
  if (!file || !allowedTypes) return false;
  const extension = file?.name?.split(".")?.pop()?.toLowerCase();
  return allowedTypes.includes(extension);
};

/**
 * Check if string is empty or only whitespace
 * @param {string} str - Input string
 * @returns {boolean} - True if empty
 */
export const isEmpty = (str) => {
  return !str || str.trim().length === 0;
};

// ============================================
// NUMBER FORMATTING UTILITIES
// ============================================

/**
 * Format number as currency (INR)
 * @param {number} amount - Amount to format
 * @returns {string} - Formatted currency string
 */
export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return "-";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
};

/**
 * Format number with thousand separators
 * @param {number} num - Number to format
 * @returns {string} - Formatted number string
 */
export const formatNumber = (num) => {
  if (num === null || num === undefined) return "-";
  return new Intl.NumberFormat("en-IN").format(num);
};

/**
 * Format file size in human-readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted file size (e.g., "2.5 MB")
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  if (!bytes) return "-";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

// ============================================
// FILE UPLOAD UTILITIES
// ============================================

/**
 * Create FormData from file and additional fields
 * @param {File|Array<File>} files - File(s) to upload
 * @param {Object} additionalFields - Additional form fields
 * @param {string} fileFieldName - Field name for file (default: 'file')
 * @returns {FormData} - FormData object ready for API submission
 *
 * Usage:
 * const formData = createFormDataWithFile(
 *   file,
 *   { document_type: 1, description: 'Test' },
 *   'file'
 * );
 */
export const createFormDataWithFile = (
  files,
  additionalFields = {},
  fileFieldName = "file",
) => {
  const formData = new FormData();

  // Handle single or multiple files
  const fileList = Array.isArray(files) ? files : [files];

  fileList.forEach((file) => {
    // Extract actual file object (handles Ant Design Upload component structure)
    const actualFile = file.originFileObj || file;
    formData.append(fileFieldName, actualFile);
  });

  // Append additional fields
  Object.entries(additionalFields).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      formData.append(key, value);
    }
  });

  return formData;
};

/**
 * Extract actual file from Ant Design Upload file object
 * @param {Object|File} file - File object from Ant Design Upload
 * @returns {File} - Actual File object
 */
export const extractActualFile = (file) => {
  return file.originFileObj || file;
};

/**
 * Get file extension from filename
 * @param {string} filename - Filename
 * @returns {string} - File extension with dot (e.g., '.pdf')
 */
export const getFileExtension = (filename) => {
  if (!filename) return "";
  const ext = filename.split(".").pop().toLowerCase();
  return `.${ext}`;
};

/**
 * Validate multiple files
 * @param {Array<File>} files - Files to validate
 * @param {Object} options - Validation options
 * @param {number} options.maxSize - Max size in bytes
 * @param {Array<string>} options.allowedExtensions - Allowed extensions
 * @param {number} options.maxCount - Maximum number of files
 * @returns {Object} - { valid: boolean, errors: Array<string> }
 */
export const validateFiles = (
  files,
  { maxSize, allowedExtensions, maxCount } = {},
) => {
  const errors = [];

  if (maxCount && files.length > maxCount) {
    errors.push(`Maximum ${maxCount} files allowed`);
    return { valid: false, errors };
  }

  files.forEach((file) => {
    if (!file) return;
    const actualFile = extractActualFile(file);

    // Size validation
    if (maxSize && !isFileSizeValid(actualFile, maxSize / (1024 * 1024))) {
      errors.push(
        `File "${actualFile?.name}" exceeds maximum size of ${formatFileSize(
          maxSize,
        )}`,
      );
    }

    // Type validation
    if (allowedExtensions && allowedExtensions.length > 0) {
      const ext = getFileExtension(actualFile?.name);
      if (!allowedExtensions.includes(ext)) {
        errors.push(
          `File "${
            actualFile?.name
          }" has invalid type. Allowed: ${allowedExtensions.join(", ")}`,
        );
      }
    }
  });

  return {
    valid: errors.length === 0,
    errors,
  };
};

// ============================================
// CONTRACT SPECIFIC UTILITIES
// ============================================

/**
 * Create a blank draft contract
 * @returns {Promise<number|null>} - Contract ID or null on error
 *
 * Used when user wants to add data to other tabs before filling basic details
 */
export const createBlankDraftContract = async (isParent = true, id) => {
  try {
    // Get law firm and organization IDs from Redux store
    const state = store.getState();
    const { lawFirmId, organizationId } = state.auth;

    const payload = {
      data: {
        status: "draft",
        organisation: organizationId,
        law_firm: lawFirmId,
        ...(!isParent && { contract_parent_id: id }),
      },
    };

    const response = await ContractService.addContract(payload);
    const success = response?.status === true;

    if (success && response.data?.id) {
      return response.data.id;
    }

    notification.error({
      message: "Error",
      description:
        "We encountered an issue while automatically creating a draft contract. Please create the contract manually.",
    });

    return null;
  } catch (err) {
    notification.error({
      message: "Error",
      description:
        "An unexpected error occurred while creating a draft contract.",
    });

    console.error("Draft Creation Error:", err);
    return null;
  }
};

// ============================================
// NOTIFICATION UTILITIES
// ============================================

/**
 * Show success notification
 * @param {string} message - Notification message
 * @param {string} description - Optional description
 */
export const showSuccessNotification = (message, description = "") => {
  notification.success({
    message,
    description,
    placement: "topRight",
  });
};

/**
 * Show error notification
 * @param {string} message - Notification message
 * @param {string} description - Optional description
 */
export const showErrorNotification = (message, description = "") => {
  notification.error({
    message,
    description,
    placement: "topRight",
  });
};

/**
 * Show warning notification
 * @param {string} message - Notification message
 * @param {string} description - Optional description
 */
export const showWarningNotification = (message, description = "") => {
  notification.warning({
    message,
    description,
    placement: "topRight",
  });
};

// ============================================
// ARRAY UTILITIES
// ============================================

/**
 * Remove duplicates from array of objects by key
 * @param {Array} array - Input array
 * @param {string} key - Key to check for duplicates
 * @returns {Array} - Array without duplicates
 */
export const removeDuplicatesByKey = (array, key) => {
  if (!Array.isArray(array)) return [];
  const seen = new Set();
  return array.filter((item) => {
    const keyValue = item[key];
    if (seen.has(keyValue)) return false;
    seen.add(keyValue);
    return true;
  });
};

/**
 * Sort array of objects by key
 * @param {Array} array - Input array
 * @param {string} key - Key to sort by
 * @param {string} order - 'asc' or 'desc'
 * @returns {Array} - Sorted array
 */
export const sortByKey = (array, key, order = "asc") => {
  if (!Array.isArray(array)) return [];
  return [...array].sort((a, b) => {
    const aVal = a[key];
    const bVal = b[key];
    if (order === "asc") {
      return aVal > bVal ? 1 : -1;
    }
    return aVal < bVal ? 1 : -1;
  });
};

/**
 * Sort dropdown options by label (case-insensitive)
 * @param {Array} options - Array of dropdown options with label/name property
 * @param {string} labelKey - Key to sort by (default: "label", can also be "name")
 * @returns {Array} - Sorted array of options
 *
 * Usage:
 * const sortedOptions = sortOptionsByLabel(documentTypeOptions);
 * const sortedByName = sortOptionsByLabel(organizationList, "name");
 */
export const sortOptionsByLabel = (options, labelKey = "label") => {
  if (!Array.isArray(options)) return [];
  return [...options].sort((a, b) => {
    const aVal = (a[labelKey] || a.name || a.label || "")
      .toString()
      .toLowerCase();
    const bVal = (b[labelKey] || b.name || b.label || "")
      .toString()
      .toLowerCase();
    return aVal.localeCompare(bVal);
  });
};

export const formatIndianPhone = (phone) => {
  if (!phone) return "";

  const digits = phone.toString().replace(/\D/g, "");
  const last10 = digits.slice(-10);

  return `+91 ${last10.replace(/(\d{5})(\d{5})/, "$1 $2")}`;
};

export const formatINR = (value) => {
  if (value === null || value === undefined || value === "") return "₹0";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(value);
};

// ============================================
// COUNTRY/STATE UTILITIES
// ============================================

/**
 * Find state ID from state name or ID
 * Handles various formats: numeric ID, string name, or numeric string
 * @param {string|number} stateValue - State value from backend
 * @param {Array} statesList - List of states from react-country-state-city
 * @returns {number|null} - Matched state ID or null
 *
 * Usage:
 * const stateId = findStateId(contract.site_state, statesList);
 */
export const findStateId = (stateValue, statesList) => {
  if (!stateValue || !statesList || statesList.length === 0) return null;

  // If it's already a numeric ID
  if (typeof stateValue === "number") {
    return stateValue;
  }

  // Try to find state by exact name match (case-insensitive)
  let state = statesList.find(
    (s) => s?.name?.toLowerCase() === stateValue?.toLowerCase(),
  );

  // If not found, try partial match
  if (!state) {
    state = statesList.find(
      (s) =>
        s?.name?.toLowerCase()?.includes(stateValue?.toLowerCase()) ||
        stateValue?.toLowerCase()?.includes(s?.name?.toLowerCase()),
    );
  }

  // If still not found and it's a numeric string, try using it as ID
  if (!state && !isNaN(stateValue)) {
    return parseInt(stateValue);
  }

  return state?.id || null;
};

/**
 * Find country by ISO2 code
 * @param {string} iso2Code - ISO2 country code (e.g., "IN", "US")
 * @param {Array} countriesList - List of countries from react-country-state-city
 * @returns {Object|null} - Country object or null
 *
 * Usage:
 * const country = findCountryByISO2("IN", countriesList);
 */
export const findCountryByISO2 = (iso2Code, countriesList) => {
  if (!iso2Code || !countriesList || countriesList.length === 0) return null;
  return countriesList.find((c) => c?.iso2 === iso2Code) || null;
};

// ============================================
// STATUS RENDERING UTILITIES
// ============================================

/**
 * Format status value for display
 * Converts snake_case/kebab-case to Title Case
 * @param {string} status - Status value
 * @returns {string} - Formatted status
 *
 * Examples:
 * formatStatusDisplay("in_progress") -> "In Progress"
 * formatStatusDisplay("pending") -> "Pending"
 */
export const formatStatusDisplay = (status) => {
  if (!status) return "";
  return toTitleCase(status);
};

/**
 * Get complete status configuration
 * Returns color, class, and display text for a status
 *
 * @param {string} status - Status value
 * @param {Object} customStatusMap - Optional custom status mapping
 * @param {Function} t - Optional translation function
 * @returns {Object} - { color, className, text }
 *
 * Usage:
 * const statusInfo = getStatusConfig("in_progress");
 * // Returns: { color: "#4f88ff", className: "status-in-progress", text: "In Progress" }
 *
 * With translation:
 * const statusInfo = getStatusConfig("in_progress", {}, t);
 * // Returns: { color: "#4f88ff", className: "status-in-progress", text: t("task_status.in_progress") }
 */
export const getStatusConfig = (status, customStatusMap = {}, t = null) => {
  const defaultStatusMap = {
    pending: {
      color: "#f29a2e",
      className: "status-pending",
      text: t ? t("task_status.pending") : "Pending",
    },
    in_progress: {
      color: "#4f88ff",
      className: "status-in-progress",
      text: t ? t("task_status.in_progress") : "In Progress",
    },
    completed: {
      color: "#18c47c",
      className: "status-completed",
      text: t ? t("task_status.completed") : "Completed",
    },
    draft: {
      color: "#e53935",
      className: "status-draft",
      text: "Draft",
    },
    active: {
      color: "#18c47c",
      className: "status-active",
      text: "Active",
    },
    inactive: {
      color: "#e53935",
      className: "status-inactive",
      text: "Inactive",
    },
    delayed: {
      color: "#e53935",
      className: "status-delayed",
      text: t ? t("task_status.delayed") : "Delayed",
    },
  };

  const statusMap = { ...defaultStatusMap, ...customStatusMap };
  const normalizedStatus = status?.toLowerCase().replace(/\s+/g, "_");

  return (
    statusMap[normalizedStatus] || {
      color: "#9aa4b2",
      className: "status-tag",
      text: formatStatusDisplay(status),
    }
  );
};

// Convert price into Indian format with EXACT value (no rounding)
export const formatIndianPrice = (value) => {
  if (value === null || value === undefined || value === "") return "";

  const num = Number(value);
  if (isNaN(num)) return "";

  if (num >= 10000000) {
    return `${num / 10000000} Cr`;
  }

  if (num >= 100000) {
    return `${num / 100000} Lac`;
  }

  if (num >= 1000) {
    return `${num / 1000} K`;
  }

  return String(num);
};

export const autoFormatString = (value) => {
  if (!value) return "";

  return value
    .trim()
    .replace(/_/g, " ") // underscores → spaces
    .replace(/\s+/g, " ") // collapse multiple spaces
    .toLowerCase() // make all lowercase
    .replace(/\b\w/g, (char) => char.toUpperCase()); // capitalize each word
};

//upload progress handler
export const createProgressHandler = (onProgress) => {
  let lastLoaded = 0;
  let lastTime = Date.now();

  return (progressEvent) => {
    if (!onProgress || typeof onProgress !== "function") {
      return;
    }

    const loaded = progressEvent.loaded;
    const total = progressEvent.total;
    const percent = Math.round((loaded * 100) / total);

    const now = Date.now();
    const timeDiff = (now - lastTime) / 1000; // seconds
    const bytesDiff = loaded - lastLoaded;

    // Calculate speed (bytes/sec)
    const speed = timeDiff > 0 ? bytesDiff / timeDiff : 0;

    // Calculate ETA (seconds)
    const remainingBytes = total - loaded;
    const eta = speed > 0 ? Math.round(remainingBytes / speed) : 0;

    // Update last values for next calculation
    lastLoaded = loaded;
    lastTime = now;

    // Call the callback with calculated data
    onProgress({
      loaded,
      total,
      percent,
      speed,
      eta,
    });
  };
};

// Used in a date picker to disable all dates outside the given start and end range
export const getDisabledDates = (startDate, endDate) => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);

  return (current) => {
    if (!current) return false;
    // Disable dates before contract start or after contract end
    return current.isBefore(start, "day") || current.isAfter(end, "day");
  };
};

// Formats byte size into a human-readable string (e.g., KB, MB, GB)
export const formatBytes = (bytes) => {
  if (!bytes) return "0 B";
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + " " + sizes[i];
};

// Formats network speed using the byte formatter (e.g., MB/s, GB/s)
export const formatSpeed = (bytesPerSec) => {
  if (!bytesPerSec) return "0 B/s";
  return formatBytes(bytesPerSec) + "/s";
};

/**
 * Formats a duration (in seconds) into the nearest readable unit.
 * @param {number} sec - Time in seconds.
 * @returns {string} Formatted time string (e.g., 30s, 5m, 2h, 1d).
 */
export const formatETA = (sec) => {
  if (!sec || sec <= 0) return "0s";

  const units = [
    { limit: 60, divisor: 1, suffix: "s" }, // < 1 min
    { limit: 3600, divisor: 60, suffix: "m" }, // < 1 hour
    { limit: 86400, divisor: 3600, suffix: "h" }, // < 1 day
    { limit: Infinity, divisor: 86400, suffix: "d" }, // days
  ];

  const u = units.find((u) => sec < u.limit);
  const value = Math.floor(sec / u.divisor);

  return `${value}${u.suffix}`;
};

export const getUploadStatusColor = (status) => {
  switch (status) {
    case UploadStatus.COMPLETED:
      return "success";
    case UploadStatus.ERROR:
      return "error";
    case UploadStatus.CANCELLED:
      return "default";
    case UploadStatus.UPLOADING:
      return "processing";
    default:
      return "default";
  }
};

/**
 * Resolve default organization for single-tenant mode
 * Fetches organization by name first, falls back to ID if not found
 */
export const resolveDefaultOrganization = async (
  getOrganisationsAPI,
  defaultOrgName,
  defaultOrgId,
) => {
  const extractOrganizations = (response) =>
    response?.data?.organisations ?? [];

  try {
    //Try resolving by organization name
    if (defaultOrgName) {
      const response = await getOrganisationsAPI({
        search: defaultOrgName,
      });

      const organizations = extractOrganizations(response);

      const matchedOrg = organizations.find(
        ({ name }) => name?.toLowerCase() === defaultOrgName.toLowerCase(),
      );

      if (matchedOrg) return matchedOrg;
    }

    //Fallback: Try resolving by organization ID
    if (defaultOrgId) {
      const response = await getOrganisationsAPI({
        id: defaultOrgId,
      });

      const organizations = extractOrganizations(response);

      const matchedOrg = organizations.find(({ id }) => id === defaultOrgId);

      if (matchedOrg) return matchedOrg;
    }

    console.error(
      "Default organization resolution failed: no match found by name or ID.",
    );
    return null;
  } catch (error) {
    console.error("Error while resolving default organization:", error);
    return null;
  }
};

/*
 * Download document by document ID, model name, and contract ID
 * Automatically triggers browser download using API response headers
 */
export const downloadDocument = async ({
  documentId,
  modelName,
  contractId,
}) => {
  if (!documentId || !modelName || !contractId) {
    notification.error({
      message: "Download Failed",
      description: "Missing required document details.",
    });
    return;
  }

  try {
    const response = await MasterService.getDocument(
      {
        data: {
          document_id: documentId,
          model_name: modelName,
          contract_id: contractId,
        },
      },
      {
        responseType: "blob",
      },
    );

    // 1. Prefer X-Filename (custom header)
    let fileName = response.headers?.["x-filename"] || "document";

    // 2. Fallback: Content-Disposition
    const contentDisposition = response.headers?.["content-disposition"];
    if (contentDisposition) {
      const match = contentDisposition.match(
        /filename\*=UTF-8''(.+)|filename="?([^"]+)"?/,
      );
      fileName = decodeURIComponent(match?.[1] || match?.[2] || fileName);
    }

    // 3. Create Blob explicitly
    const blob = new Blob([response.data], {
      type: response.data.type || "application/octet-stream",
    });

    // 4. Trigger browser download
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = fileName;

    document.body.appendChild(link);
    link.click();

    // 5. Cleanup
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);

    notification.success({
      message: "Download Started",
      description: `${fileName} is being downloaded.`,
    });
  } catch (error) {
    console.error("Document download error:", error);

    let errorMessage = "Unable to download the document.";

    if (error?.response?.status === 403) {
      errorMessage = "You are not authorized to download this document.";
    } else if (error?.response?.status === 404) {
      errorMessage = "The requested document was not found.";
    }

    notification.error({
      message: "Download Failed",
      description: errorMessage,
    });
  }
};

// Transform Risk Category Result
export const normalizeRisk = (input) => {
  const value = input.toLowerCase().trim();

  if (value.includes("low")) return "Low Risk";
  if (value.includes("medium")) return "Medium Risk";
  if (value.includes("high")) return "High Risk";

  return "Unknown Risk";
};

// Return the initial of a user name
// Used in CommunicationCard, UpdateCard, EventUpdateCard
export const getUserInitials = (user) => {
  if (!user) return "U";
  if (user?.full_name) {
    const names = user.full_name.trim().split(" ");
    if (names.length >= 2) {
      return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    }
    return names[0][0].toUpperCase();
  }
  const firstName = user?.first_name || "";
  const lastName = user?.last_name || "";
  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  }
  if (firstName) return firstName[0].toUpperCase();
  return "U";
};

// Return the Display Name of a User
// Used in CommunicationCard, UpdateCard, EventUpdateCard
export const getUserDisplayName = (user) => {
  if (!user) return null;
  return (
    user?.full_name ||
    `${user?.first_name || ""} ${user?.last_name || ""}`.trim() ||
    null
  );
};

// Disable all dates before today in a DatePicker
// Used in ProjectRemindersSection
export const disablePastDates = (current) =>
  current && current < dayjs().startOf("day");

// Format a reminder_date value for display in the reminders table
// Used in ProjectRemindersSection
export const formatReminderDate = (record) => {
  if (!record?.reminder_date) return "-";
  const parsed = dayjs(record?.reminder_date);
  return parsed.isValid() ? parsed.format("DD MMM YYYY") : record?.reminder_date;
};

// Format a date string into a relative time format (e.g., "Just now", "5m ago", "2h ago", "3d ago")
// Used in Upload Notification center
export const formatRelativeTime = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffSec < 60) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString();
};

// ============================================
// TABLE RENDER UTILITIES
// ============================================

export const renderTruncated = (text) => {
  if (!text) return "-";
  return (
    <Tooltip title={text}>
      <StyledTruncatedText>{text}</StyledTruncatedText>
    </Tooltip>
  );
};